import { kTimeTextCore } from "../../../Admin/kReusableCoreTS/kTimeTextCore";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppHistoryAnswer, AppHistoryRow } from "./AppSendHistory";
import { AppBubbleKinds, IKindsDoc, IKindsNode } from "./AppBubbleKinds";
import { AppSource } from "./AppSource";
import { AppChatRules, AppChatWord } from "./AppChatRules";
import { AppAssistRules } from "./AppAssistRules";
import { AppLinkNodes, ILinkDoc, ILinkNode } from "./AppLinks";
import { AppEditMark } from "./AppLiveEvents";
import { AppTranslateBubble, ITrDoc, ITrNode } from "./AppTranslateBubble";
import { AppTranslateRules } from "./AppTranslateRules";

// AppChatAppend - draws the owner's own sent messages into the open conversation, before the next
// rebuild writes them into the page. `AppChatRules` decides which and what they say; this is the act.
//
// ⭐ TWO WAYS IN. (a) `Add` - the owner pressed Send in this visit, the bubble appears at once and
// its status line follows the send poll. (b) the frame loaded - this subscriber's `/send` history is
// asked once, and whatever this chat sent since the page was built is drawn (`AppChatRules.Keep`).
//
// ⛔ THE MESSAGE TEXT IS `textContent`, NEVER MARKUP. It is what the owner typed, and a `<b>` in it
// is a `<b>` they typed.
//
// ⚠ SHAPED BY INTERFACES, like `AppMediaFill`, so the suite drives it with a tiny fake document.
// ⚠ A FRAME THAT IS REPLACED IS `Stop`ped: a late history answer must not draw into the next page.

export interface IChatNode {
    textContent: string | null;
    className: string;
    firstElementChild: IChatNode | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: IChatNode): unknown;
    insertBefore(Child: IChatNode, Ref: IChatNode | null): unknown;
    querySelector(Selector: string): IChatNode | null;
    querySelectorAll(Selector: string): ArrayLike<IChatNode>;
    scrollIntoView(Arg?: ScrollIntoViewOptions): void;
}

export interface IChatDoc {
    /** The page's `<html>` - where the embedding mark goes. Optional so a bare test fake still fits. */
    documentElement?: IChatNode | null;
    querySelector(Selector: string): IChatNode | null;
    querySelectorAll(Selector: string): ArrayLike<IChatNode>;
    createElement(Tag: string): IChatNode;
}

export interface IChatFrame {
    contentDocument: IChatDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

/** One bubble to draw. */
export class AppChatItem {
    public Id: string = "";
    public Text: string = "";
    /** The UTC minute, 12 digits - picks the day it belongs under. */
    public Minute: string = "";
    public Clock: string = "";
    public Word: AppChatWord = new AppChatWord();
    /**
     * ⭐ A LIVE MESSAGE (`AppLiveFeed`, `/live`): its WhatsApp id. Set = the bubble is drawn as the page draws a
     * message (`data-mid`, `Side`, media line, no status line); "" = the owner's own send from this app (`Id`).
     */
    public Mid: string = "";
    /** "them" or "me" - only read for a live message (`Mid` set). */
    public Side: string = "them";
    /** A live media message's placeholder line (the page's `.nomedia` wording); "" for text. */
    public Media: string = "";
    /**
     * ⭐ TRANSLATION (2026-09-27): what the bubble's flag reveals - a live message's ORIGINAL (`AppLiveRules.OriginalOf`),
     * or, on the owner's own send, the words that WENT OUT (`sentText`). "" = no flag.
     */
    public Original: string = "";
    /** (2026-10-20) A reply sent from this app: the WhatsApp id of the message it answers ("" = not a reply). */
    public QuoteOf: string = "";
    /** ⛔ A translated send whose words could not be translated - nothing was sent (Retry / Send in English). */
    public Failed: boolean = false;
    /** ⭐ (2026-10-01) A live message that was EDITED before it reached the page: its bubble gets the "edited" mark. */
    public Edited: boolean = false;
}

/** Fetches a live media bubble's file from its download button. */
export type AppLiveMediaFetch = (Button: unknown) => void;

export class AppChatAppend {
    /** The request id on an appended bubble - the seam a test (and `SetState`) finds it by. */
    public static readonly IdAttr: string = "data-wac-id";
    /** The WhatsApp message id the service writes on every page bubble. */
    public static readonly MidAttr: string = "data-mid";
    public static readonly DayAttr: string = "data-wac-day";
    /** The page's own day checkbox id (`DossierHtml.DayGroup`): `d-` + the subscriber's `YYYYMMDD`. */
    public static readonly PageDayId: RegExp = new RegExp("^d-([0-9]{8})$");
    public static readonly StateClass: string = "wac-state";

    private Source: AppSource;
    private ChatId: string;
    private Frame: IChatFrame | null = null;
    private Doc: IChatDoc | null = null;
    private Pending: AppChatItem[] = [];
    private Stopped: boolean = false;

    public constructor(Source: AppSource, ChatId: string) {
        this.Source = Source;
        this.ChatId = nz(ChatId);
    }

    public Attach = (Frame: IChatFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Attach");
        }
    };

    /** The page is in: draw anything sent before it arrived, then ask for the history once. */
    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Frame === null || this.Frame.contentDocument === null) { return; }
            if (this.Doc !== null) { return; }
            this.Doc = this.Frame.contentDocument;
            AppChatAppend.MarkEmbedded(this.Doc as IChatDoc);
            const Waiting: AppChatItem[] = this.Pending;
            this.Pending = [];
            for (const One of Waiting) { this.Place(One); }
            if (this.Source.CanHistory() === true && this.ChatId.length > 0) {
                this.Source.History(this.OnHistory);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.OnLoad");
        }
    };

    /**
     * ⭐ THE EMBEDDING CONTRACT WITH THE PAGE (2026-09-25): `<html data-wac-embedded="1">` tells the
     * contact page it is inside the app's chat card, and it lays itself out as a chat - compact
     * header, the thread down to the frame's bottom edge, Details beside it or in a drawer.
     * ⚠ The markup already carries it (`AppAssistRules.Embedded`, so there is no jump); this is the
     * contract's own step, and it covers a page whose `<html>` the string rule could not find.
     */
    public static MarkEmbedded = (Doc: IChatDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Root: IChatNode | null | undefined = Doc.documentElement;
            if (Root === undefined || Root === null) { return; }
            (Root as IChatNode).setAttribute(AppAssistRules.EmbeddedAttr, "1");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.MarkEmbedded");
        }
    };

    /** (a) A send this visit made. `AtIso` is when it was pressed. */
    public Add = (Id: string, Text: string, AtIso: string, Word: AppChatWord): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || nz(Id).length === 0) { return; }
            const Item: AppChatItem = AppChatAppend.ItemOf(Id, Text, AtIso, Word);
            if (this.Doc === null) { this.Pending.push(Item); return; }
            if (this.Has(Item.Id) === true) { return; }
            this.Place(Item);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Add");
        }
    };

    /**
     * (2026-10-20) A REPLY this visit made: drawn at once, like `Add`, with the quote block of the message it answers
     * (`AppBubbleKinds.QuoteFrom` - read off that bubble on the page, `textContent` only). The echo adopts it (`AdoptSent`).
     */
    public AddReply = (Id: string, Text: string, AtIso: string, Word: AppChatWord, QuotedMid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || nz(Id).length === 0) { return; }
            const Item: AppChatItem = AppChatAppend.ItemOf(Id, Text, AtIso, Word);
            Item.QuoteOf = nz(QuotedMid);
            if (this.Doc === null) { this.Pending.push(Item); return; }
            if (this.Has(Item.Id) === true) { return; }
            this.Place(Item);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.AddReply");
        }
    };

    /** The poll said something new about message `Id`. */
    public SetState = (Id: string, Word: AppChatWord): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of this.Pending) {
                if (One.Id === Id) { One.Word = Word; }
            }
            const Bubble: IChatNode | null = this.Find(Id);
            if (Bubble === null) { return; }
            const Line: IChatNode | null = Bubble.querySelector("." + AppChatAppend.StateClass);
            if (Line === null) { return; }
            Line.textContent = Word.Text;
            Line.className = AppChatAppend.ClassFor(Word.Bad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.SetState");
        }
    };

    /** ⭐ TRANSLATION: message `Id` went out in other words - its bubble gets the flag that shows them (+ Copy). */
    public SetSentText = (Id: string, SentText: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(SentText).length === 0) { return; }
            for (const One of this.Pending) { if (One.Id === Id) { One.Original = SentText; } }
            const Bubble: IChatNode | null = this.Find(Id);
            if (Bubble === null || this.Doc === null) { return; }
            AppTranslateBubble.Mark(this.Doc as unknown as ITrDoc, Bubble as unknown as ITrNode, SentText, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.SetSentText");
        }
    };

    /** ⛔ TRANSLATION: message `Id` could not be translated - NOTHING was sent. Its bubble offers Retry / Send in English. */
    public SetFailed = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of this.Pending) { if (One.Id === Id) { One.Failed = true; } }
            const Bubble: IChatNode | null = this.Find(Id);
            if (Bubble === null || this.Doc === null) { return; }
            AppTranslateBubble.Failed(this.Doc as unknown as ITrDoc, Bubble as unknown as ITrNode);
            // ⚠ Retry / Send in English must be SEEN: the bubble grew, so it is brought fully into view.
            if (typeof Bubble.scrollIntoView === "function") { Bubble.scrollIntoView({ block: "end" }); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.SetFailed");
        }
    };

    /**
     * (b) This subscriber's sent history. ⚠ A refusal or a "still loading" answer draws nothing and
     * says nothing - the page is complete without it, and the Sent tab is where the list is read.
     */
    public OnHistory = (Answer: AppHistoryAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Doc === null || Answer.Ok !== true) { return; }
            const Doc: IChatDoc = this.Doc as IChatDoc;
            const Rows: AppHistoryRow[] = AppChatRules.Keep(Answer.Rows, this.ChatId,
                AppChatRules.Newest(AppChatAppend.Values(Doc, "id", ".b")),
                AppChatAppend.Values(Doc, AppChatAppend.MidAttr, "[" + AppChatAppend.MidAttr + "]"),
                AppChatAppend.Values(Doc, AppChatAppend.IdAttr, "[" + AppChatAppend.IdAttr + "]"));
            for (const Row of Rows) {
                const Item: AppChatItem = AppChatAppend.ItemOf(Row.Id, AppChatRules.TextOf(Row), Row.At,
                    AppChatRules.WordForState(Row.State));
                Item.Original = nz(Row.SentText);
                // ⛔ A failed row offers Retry / Send in English only until the owner already did one of them.
                Item.Failed = AppTranslateRules.IsFailed(Row) === true && AppTranslateRules.Superseded(Row, Answer.Rows) === false;
                this.Place(Item);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.OnHistory");
        }
    };

    /**
     * ⭐ SEVERAL TABS (2026-09-27): another tab queued a message to this chat - ask the sent history again,
     * so its bubble appears here too. The same path as a page open (`OnHistory`): a bubble whose request id
     * or message id is already on the page is never drawn twice. ⛔ It READS; nothing is sent.
     */
    public Refresh = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Doc === null || this.ChatId.length === 0) { return; }
            if (this.Source.CanHistory() === true) { this.Source.History(this.OnHistory); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Refresh");
        }
    };

    /**
     * The chat this appender draws into (its WhatsApp chat id), "" for none.
     * DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public Chat = (): string => {
        return this.ChatId;
    };

    /** The frame is being replaced. */
    public Stop = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Stopped = true;
            this.Pending = [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Stop");
        }
    };

    private static ItemOf = (Id: string, Text: string, AtIso: string, Word: AppChatWord): AppChatItem => {
        if (getCoreUtils().KErrorMode === true) { return new AppChatItem(); }
        try {
            const Item: AppChatItem = new AppChatItem();
            Item.Id = nz(Id);
            Item.Text = nz(Text);
            Item.Minute = AppChatRules.MinuteOfIso(AtIso);
            Item.Clock = kTimeTextCore.ClockOfIso(AtIso);
            Item.Word = Word;
            return Item;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.ItemOf");
            return new AppChatItem();
        }
    };

    /** Every value of `Attr` on the elements `Selector` finds - compared in a loop, never in a selector. */
    public static Values = (Doc: IChatDoc, Attr: string, Selector: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            const Found: ArrayLike<IChatNode> = Doc.querySelectorAll(Selector);
            for (let At: number = 0; At < Found.length; At = At + 1) {
                const Value: string | null = Found[At].getAttribute(Attr);
                if (Value !== null && Value.length > 0) { Out.push(Value); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Values");
            return [];
        }
    };

    private Has = (Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Find(Id) !== null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Has");
            return false;
        }
    };

    /** The appended bubble for request `Id`. ⛔ Compared in a loop - the id never enters a selector. */
    private Find = (Id: string): IChatNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Doc === null) { return null; }
            const Found: ArrayLike<IChatNode> = (this.Doc as IChatDoc).querySelectorAll("[" + AppChatAppend.IdAttr + "]");
            for (let At: number = 0; At < Found.length; At = At + 1) {
                if (Found[At].getAttribute(AppChatAppend.IdAttr) === Id) { return Found[At]; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Find");
            return null;
        }
    };

    /** Draw one bubble under its day and bring it into view. ⚠ A page with no conversation pane gets none. */
    private Place = (Item: AppChatItem): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            const Doc: IChatDoc = this.Doc as IChatDoc;
            const Chat: IChatNode | null = Doc.querySelector(".chat");
            if (Chat === null) { return; }
            const Rows: IChatNode | null = this.RowsFor(Doc, Chat as IChatNode, Item.Minute);
            if (Rows === null) { return; }
            const Bubble: IChatNode = AppChatAppend.BubbleOf(Doc, Item);
            if (Item.QuoteOf.length > 0) { AppBubbleKinds.QuoteFrom(Doc as unknown as IKindsDoc, Bubble as unknown as IKindsNode, Item.QuoteOf); }
            (Rows as IChatNode).appendChild(Bubble);
            if (typeof Bubble.scrollIntoView === "function") { Bubble.scrollIntoView({ block: "end" }); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.Place");
        }
    };

    /**
     * The `.dayrows` a bubble of this minute belongs in: the page's newest day when it is the same
     * day, else a day this page added (made once, placed as the newest - `.chat` is
     * `column-reverse`, so its FIRST child is the bottom of the conversation).
     * ⛔ THE READER'S DAY, NOT UTC (2026-09-28): the page's days are the subscriber's own (`d-YYYYMMDD` in their zone),
     * and a bubble's minute is UTC - at UTC+7 a message at 06:00 local went under YESTERDAY, and one after 07:00 local
     * made a second "today" group beside the page's own. `AppChatRules.LocalDay` = the browser's day.
     */
    private RowsFor = (Doc: IChatDoc, Chat: IChatNode, Minute: string): IChatNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Key: string = AppChatRules.LocalDay(Minute);
            const First: IChatNode | null = Chat.querySelector(".day");
            if (First !== null && AppChatAppend.DayKeyOf(First as IChatNode) === Key) {
                return (First as IChatNode).querySelector(".dayrows");
            }
            const Days: ArrayLike<IChatNode> = Chat.querySelectorAll("[" + AppChatAppend.DayAttr + "]");
            for (let At: number = 0; At < Days.length; At = At + 1) {
                if (Days[At].getAttribute(AppChatAppend.DayAttr) === Key) { return Days[At].querySelector(".dayrows"); }
            }
            const Day: IChatNode = AppChatAppend.DayOf(Doc, Key);
            Chat.insertBefore(Day, Chat.firstElementChild);
            return Day.querySelector(".dayrows");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.RowsFor");
            return null;
        }
    };

    /**
     * A day's `YYYYMMDD`: the one this app stamped on it, else the page's own (its checkbox is `d-YYYYMMDD`, the
     * subscriber's day - `DossierHtml.DayGroup`), else (a page from before days had ids) the reader's day of its
     * newest bubble.
     */
    private static DayKeyOf = (Day: IChatNode): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Own: string | null = Day.getAttribute(AppChatAppend.DayAttr);
            if (Own !== null && Own.length > 0) { return Own; }
            const Box: IChatNode | null = Day.querySelector(".daybox");
            if (Box !== null) {
                const Found: RegExpExecArray | null = AppChatAppend.PageDayId.exec(nz(Box.getAttribute("id")));
                if (Found !== null) { return Found[1]; }
            }
            const Ids: string[] = [];
            const Bubbles: ArrayLike<IChatNode> = Day.querySelectorAll(".b");
            for (let At: number = 0; At < Bubbles.length; At = At + 1) { Ids.push(nz(Bubbles[At].getAttribute("id"))); }
            return AppChatRules.LocalDay(AppChatRules.Newest(Ids));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.DayKeyOf");
            return "";
        }
    };

    /** A new day group, shaped exactly like the page's own (`DossierHtml.DayGroup`), open. */
    private static DayOf = (Doc: IChatDoc, Key: string): IChatNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("div"); }
        try {
            const Day: IChatNode = Doc.createElement("div");
            Day.className = "day";
            Day.setAttribute(AppChatAppend.DayAttr, Key);
            const Box: IChatNode = Doc.createElement("input");
            Box.className = "daybox";
            Box.setAttribute("type", "checkbox");
            Box.setAttribute("id", "d-wac-" + Key);
            Box.setAttribute("checked", "");
            Box.setAttribute("aria-label", AppChatRules.DayCaption(Key, Date.now()));
            const Head: IChatNode = Doc.createElement("label");
            Head.className = "dayhead";
            Head.setAttribute("for", "d-wac-" + Key);
            const Pill: IChatNode = Doc.createElement("span");
            Pill.textContent = AppChatRules.DayCaption(Key, Date.now());
            Head.appendChild(Pill);
            const Rows: IChatNode = Doc.createElement("div");
            Rows.className = "dayrows";
            Day.appendChild(Box);
            Day.appendChild(Head);
            Day.appendChild(Rows);
            return Day;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.DayOf");
            return Doc.createElement("div");
        }
    };

    /**
     * ⭐ A LIVE MESSAGE (`/live`, `AppLiveFeed`): drawn under its day like a sent one, but NOT scrolled here - the
     * feed decides (a reader at the bottom follows; one scrolled up gets a pill). Null when the page has no pane,
     * or the message is already on it (its `data-mid`). ⛔ Text by `textContent` only.
     */
    /** ⭐ Who fetches a live media bubble's file (`AppMediaFill.FetchLive`); null = the button waits for a click. */
    public OnLiveMedia: AppLiveMediaFetch | null = null;

    public PlaceLive = (Item: AppChatItem): IChatNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Stopped === true || this.Doc === null || Item.Mid.length === 0) { return null; }
            const Doc: IChatDoc = this.Doc as IChatDoc;
            if (AppChatAppend.Values(Doc, AppChatAppend.MidAttr, "[" + AppChatAppend.MidAttr + "]").indexOf(Item.Mid) >= 0) { return null; }
            const Chat: IChatNode | null = Doc.querySelector(".chat");
            if (Chat === null) { return null; }
            const Rows: IChatNode | null = this.RowsFor(Doc, Chat as IChatNode, Item.Minute);
            if (Rows === null) { return null; }
            const Bubble: IChatNode = AppChatAppend.LiveBubbleOf(Doc, Item);
            (Rows as IChatNode).appendChild(Bubble);
            AppBubbleKinds.Decorate(Doc as unknown as IKindsDoc, Bubble as unknown as IKindsNode); // (kinds, 2026-10-14) the one hook
            // ⭐ (2026-10-22) A live photo / voice note: its download button is pressed for the owner (`OnLiveMedia`).
            const Download: IChatNode | null = Bubble.querySelector("[data-wac-download]");
            if (Download !== null && this.OnLiveMedia !== null) { (this.OnLiveMedia as AppLiveMediaFetch)(Download); }
            return Bubble;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.PlaceLive");
            return null;
        }
    };

    /**
     * ⛔ ONE MESSAGE, ONE BUBBLE: a live message the owner sent FROM THIS APP is already here as a `data-wac-id`
     * bubble (no WhatsApp id yet). The newest such bubble with the same words and no `data-mid` takes the id and
     * true is answered - the feed then draws nothing. False = sent from the phone (or not on this page): draw it.
     */
    public AdoptSent = (Mid: string, Text: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Doc === null || nz(Mid).length === 0) { return false; }
            const Found: ArrayLike<IChatNode> = (this.Doc as IChatDoc).querySelectorAll("[" + AppChatAppend.IdAttr + "]");
            for (let At: number = Found.length - 1; At >= 0; At = At - 1) {
                const One: IChatNode = Found[At];
                if (One.getAttribute(AppChatAppend.MidAttr) !== null) { continue; }
                const Words: IChatNode | null = One.querySelector(".txt");
                if (Words === null || nz(Words.textContent) !== nz(Text)) { continue; }
                One.setAttribute(AppChatAppend.MidAttr, Mid);
                // (2026-10-20) The adopted bubble gets what the live echo carries too - a reply's quote block - unless
                // it was drawn with its quote when sent (`AddReply`): never a second one.
                if (One.querySelector(".wkq") !== null) {
                    // A reply has no poll of its own: its echo is WhatsApp saying it went.
                    const Line: IChatNode | null = One.querySelector("." + AppChatAppend.StateClass);
                    if (Line !== null) { Line.textContent = AppChatRules.Sent; }
                    return true;
                }
                AppBubbleKinds.Decorate(this.Doc as unknown as IKindsDoc, One as unknown as IKindsNode);
                return true;
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.AdoptSent");
            return false;
        }
    };

    /** The loaded page, or null before `load` / after `Stop`. DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Page = (): IChatDoc | null => {
        if (this.Stopped === true) { return null; }
        return this.Doc;
    };

    /** `<div class="b them|me" data-mid data-wac-live>` + media line + text + clock - the page's own shape. */
    private static LiveBubbleOf = (Doc: IChatDoc, Item: AppChatItem): IChatNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("div"); }
        try {
            const Bubble: IChatNode = Doc.createElement("div");
            let Side: string = "them";
            if (Item.Side === "me") { Side = "me"; }
            Bubble.className = "b " + Side;
            Bubble.setAttribute(AppChatAppend.MidAttr, Item.Mid);
            Bubble.setAttribute(AppChatAppend.LiveAttr, "1");
            if (Item.Media.length > 0) {
                const Media: IChatNode = Doc.createElement("span");
                Media.className = "nomedia";
                Media.textContent = Item.Media;
                Bubble.appendChild(Media);
            }
            if (Item.Text.length > 0) {
                const Words: IChatNode = Doc.createElement("span");
                Words.className = "txt";
                // ⭐ LINKS, AS THE REBUILT PAGE DRAWS THEM (`AppLinks`, 2026-10-01): a URL is an anchor the moment the
                // message shows. Built node by node; a message with no link is `textContent`, exactly as before.
                AppLinkNodes.Fill(Doc as unknown as ILinkDoc, Words as unknown as ILinkNode, Item.Text);
                Bubble.appendChild(Words);
            }
            if (Item.Edited === true) { Bubble.setAttribute(AppEditMark.Attr, "1"); }
            const Clock: IChatNode = Doc.createElement("span");
            Clock.className = "t";
            Clock.textContent = Item.Clock;
            Bubble.appendChild(Clock);
            // ⭐ TRANSLATION: a translated live message shows my language; its flag shows what was written (+ Copy).
            AppTranslateBubble.Mark(Doc as unknown as ITrDoc, Bubble as unknown as ITrNode, Item.Original, Side === "me");
            return Bubble;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.LiveBubbleOf");
            return Doc.createElement("div");
        }
    };

    /** Marks a bubble the live feed drew (`/live`) - until the next rebuild writes it into the page. */
    public static readonly LiveAttr: string = "data-wac-live";

    /** `<div class="b me" data-wac-id>` + text + status line + clock - the page's own classes. */
    private static BubbleOf = (Doc: IChatDoc, Item: AppChatItem): IChatNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("div"); }
        try {
            const Bubble: IChatNode = Doc.createElement("div");
            Bubble.className = "b me";
            Bubble.setAttribute(AppChatAppend.IdAttr, Item.Id);
            const Words: IChatNode = Doc.createElement("span");
            Words.className = "txt";
            // ⭐ A link the owner just sent is clickable at once too (`AppLinks` - the page's own rule).
            AppLinkNodes.Fill(Doc as unknown as ILinkDoc, Words as unknown as ILinkNode, Item.Text);
            const Line: IChatNode = Doc.createElement("span");
            Line.className = AppChatAppend.ClassFor(Item.Word.Bad);
            Line.textContent = Item.Word.Text;
            const Clock: IChatNode = Doc.createElement("span");
            Clock.className = "t";
            Clock.textContent = Item.Clock;
            Bubble.appendChild(Words);
            Bubble.appendChild(Line);
            Bubble.appendChild(Clock);
            // ⭐ TRANSLATION: the owner's words on the bubble; the flag shows the words that went out.
            AppTranslateBubble.Mark(Doc as unknown as ITrDoc, Bubble as unknown as ITrNode, Item.Original, true);
            if (Item.Failed === true) { AppTranslateBubble.Failed(Doc as unknown as ITrDoc, Bubble as unknown as ITrNode); }
            return Bubble;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatAppend.BubbleOf");
            return Doc.createElement("div");
        }
    };

    /** Marks a refusal, in the alarm colour (the app's `--owed`). */
    public static readonly BadClass: string = "wac-bad";

    /**
     * The status line's classes. ⛔ NOT an inline style (the CSP has no 'unsafe-inline'): `Css` rides in the
     * frame's linked sheet (`AppMain.Frame`). DOCUMENTED EXCEPTION: picks one of two constant strings.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; every caller is guarded.
     */
    public static ClassFor = (Bad: boolean): string => {
        if (Bad === true) { return AppChatAppend.StateClass + " " + AppChatAppend.BadClass; }
        return AppChatAppend.StateClass;
    };

    /**
     * The status line under a bubble drawn by the app: ink-dim, the alarm colour only for a refusal.
     * DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        return ".wac-state{display:block;font-size:11.5px;margin-top:2px;color:var(--dim)}"
            // ⭐ The frame's own `--owed` (= the app's `warning`, in the owner's theme - `AppTokens.FrameVars`): `Pairs`
            // measures it on the owner's bubble (`accent-soft`) in both themes. It was a fixed orange with a
            // system-only dark value, which ignored a chosen theme.
            + ".wac-state.wac-bad{color:var(--owed,#a5561b)}";
    };
}
